import type React from "react"
import { Box, Typography, TextField, Button, Stack, Chip, useTheme, useMediaQuery } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import FileDownloadIcon from "@mui/icons-material/FileDownload"

interface ContactsHeaderProps {
  searchText: string
  onSearchChange: (text: string) => void
  onAddContact: () => void
}

const ContactsHeader: React.FC<ContactsHeaderProps> = ({ searchText, onSearchChange, onAddContact }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.down("md"))

  return (
    <Box sx={{ mb: 3, width: "100%" }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "1.2rem", sm: "1.4rem" },
          color: "#2c3e50",
          letterSpacing: 1.5,
          textTransform: "uppercase",
          mb: { xs: 2, sm: 3 },
          fontFamily: "Roboto, sans-serif",
        }}
        gutterBottom
      >
        Contactos
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={{ xs: 2, sm: 2 }}
        width="100%"
      >
        <TextField
          variant="outlined"
          placeholder="Buscar contactos..."
          size="small"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            flex: { xs: "1 1 100%", sm: 1 },
            minWidth: { xs: "100%", sm: 250 },
            mb: { xs: 1, sm: 0 },
          }}
          InputProps={{
            sx: { borderRadius: 2 },
          }}
          fullWidth={isMobile}
        />

        <Stack
          direction={{ xs: "row", sm: "row" }}
          spacing={1.5}
          sx={{
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "space-between", sm: "flex-start" },
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              flex: isMobile ? 1 : "none",
              whiteSpace: "nowrap",
              px: { xs: 1, sm: 2 },
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
            onClick={onAddContact}
          >
            {isTablet ? "Nuevo" : "Nuevo contacto"}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<FileDownloadIcon />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              flex: isMobile ? 1 : "none",
              whiteSpace: "nowrap",
              px: { xs: 1, sm: 2 },
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            Exportar
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ overflowX: "auto", pb: 1, mb: 1, width: "100%" }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: { xs: "nowrap", md: "wrap" },
            minWidth: isMobile ? "500px" : "auto",
          }}
        >
          {["Todos", "Clientes", "Proveedores", "Empresas", "Favoritos"].map((filtro) => (
            <Chip
              key={filtro}
              label={filtro}
              variant={filtro === "Todos" ? "filled" : "outlined"}
              color="primary"
              clickable
              sx={{
                mb: { md: 1 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                height: { xs: 28, sm: 32 },
              }}
            />
          ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default ContactsHeader
